import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Book, ChevronRight, Search, Home, Gamepad2, ShoppingBag, Shield, HelpCircle, FileText, Copy, Check, Menu, Sword, Server, MessageCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { wikiContent, siteConfig } from '@/config/site';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const wikiSections = [
  { id: 'getting-started', label: 'Getting Started', icon: FileText, emoji: '🚀' },
  { id: 'tentang-hytale', label: 'Tentang Hytale', icon: Gamepad2, emoji: '🎮' },
  { id: 'faq', label: 'FAQ', icon: HelpCircle, emoji: '❓' },
  { id: 'rules', label: 'Server Rules', icon: Shield, emoji: '📜' },
];

// Generic wiki page template component
function WikiPageTemplate({ section, content }) {
  const [copied, setCopied] = useState(false);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  // Get icon component for the section
  const sectionInfo = wikiSections.find(s => s.id === section);
  const IconComponent = sectionInfo?.icon || Book;
  const emoji = sectionInfo?.emoji || '📄';
  const title = content?.title || sectionInfo?.label || 'Wiki';

  return (
    <div className="space-y-8">
      {/* Header with Copy button */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-primary flex items-center gap-3">
          <span className="text-2xl">{emoji}</span> {title}
        </h1>
        <Button
          variant="outline"
          size="sm"
          onClick={copyLink}
          className="flex items-center gap-2"
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {copied ? 'Copied!' : 'Copy'}
        </Button>
      </div>

      {/* Banner */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-transparent" />
        <div className="relative p-6 md:p-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-primary/20 flex items-center justify-center">
              <IconComponent className="w-10 h-10 md:w-12 md:h-12 text-primary" />
            </div>
            <div className="text-center md:text-left">
              <h2 className="font-display text-xl md:text-2xl font-bold text-foreground mb-1">
                {title.toUpperCase()}
              </h2>
              <p className="text-muted-foreground text-sm md:text-base">
                Wiki Hytale Indonesia - {sectionInfo?.label}
              </p>
              <div className="mt-3">
                <code className="px-3 py-1.5 rounded-lg bg-card border border-border text-xs md:text-sm font-mono">
                  {siteConfig.serverIP}
                </code>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Content */}
      <div className="prose prose-sm md:prose-base dark:prose-invert max-w-none prose-headings:font-display prose-headings:text-foreground prose-p:text-muted-foreground prose-a:text-primary prose-strong:text-foreground prose-code:text-primary prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-pre:bg-card prose-pre:border prose-pre:border-border prose-th:text-foreground prose-td:text-muted-foreground prose-li:text-muted-foreground">
        <ReactMarkdown>{content?.content || ''}</ReactMarkdown>
      </div>
    </div>
  );
}

// Welcome section component
function WelcomeSection() {
  const [copied, setCopied] = useState(false);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-8">
      {/* Header with Copy button */}
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-primary flex items-center gap-3">
          <span className="text-2xl">👋</span> Selamat Datang!
        </h1>
        <Button
          variant="outline"
          size="sm"
          onClick={copyLink}
          className="flex items-center gap-2"
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {copied ? 'Copied!' : 'Copy'}
        </Button>
      </div>

      {/* Welcome text */}
      <p className="text-muted-foreground text-lg">
        Selamat datang di Wiki resmi <span className="text-primary font-semibold">Hytale Indonesia</span>! 
        Di sini kamu bisa menemukan semua informasi yang kamu butuhkan kapan saja dan dari berbagai topik!
      </p>

      {/* Banner */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-transparent" />
        <div className="relative p-8 md:p-12">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <img
              src={siteConfig.logo}
              alt="Hytale Indonesia"
              className="w-24 h-24 md:w-32 md:h-32 object-contain"
            />
            <div className="text-center md:text-left">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-2">
                SELAMAT DATANG
              </h2>
              <h3 className="font-display text-xl md:text-2xl font-bold text-primary mb-2">
                DI WIKI HYTALE INDONESIA
              </h3>
              <p className="text-muted-foreground">
                Dibuat khusus untuk komunitas Hytale Indonesia!
              </p>
              <div className="mt-4">
                <code className="px-3 py-1.5 rounded-lg bg-card border border-border text-sm font-mono">
                  {siteConfig.serverIP}
                </code>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Navigation Guide */}
      <div className="space-y-6">
        <h2 className="font-display text-2xl font-bold text-foreground flex items-center gap-3">
          <span className="text-xl">🧭</span> Bagaimana cara navigasi wiki?
        </h2>

        <div className="space-y-4">
          <h3 className="font-semibold text-foreground">Navigasi dengan Sidebar</h3>
          <div className="space-y-3">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-primary-foreground font-bold shrink-0">
                1
              </div>
              <p className="text-muted-foreground pt-1">
                Di sebelah kiri akan muncul <span className="text-primary font-medium">navigation bar vertikal</span>
              </p>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-primary-foreground font-bold shrink-0">
                2
              </div>
              <p className="text-muted-foreground pt-1">
                <span className="text-primary font-medium">Pilih kategori</span> yang ingin kamu cari informasinya
              </p>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-primary-foreground font-bold shrink-0">
                3
              </div>
              <p className="text-muted-foreground pt-1">
                Konten akan muncul di panel utama. <span className="text-primary font-medium">Selamat membaca!</span>
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-semibold text-foreground">Daftar Konten Wiki</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {wikiSections.filter(s => s.id !== 'welcome').map((section) => {
              const Icon = section.icon;
              return (
                <div key={section.id} className="p-4 rounded-xl border border-border bg-card/50 hover:border-primary/30 transition-colors">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <span className="font-semibold text-foreground">{section.label}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {section.id === 'getting-started' && 'Panduan lengkap untuk memulai petualangan'}
                    {section.id === 'tentang-hytale' && 'Pelajari dasar-dasar Hytale dan fitur-fiturnya'}
                    {section.id === 'cara-main' && 'Step-by-step guide untuk bermain di server'}
                    {section.id === 'gameplay' && 'Combat, crafting, building, dan mechanics lainnya'}
                    {section.id === 'commands' && 'Daftar lengkap semua commands yang tersedia'}
                    {section.id === 'sistem-server' && 'Economy, leveling, protection, dan server systems'}
                    {section.id === 'faq' && 'Jawaban untuk pertanyaan yang sering diajukan'}
                    {section.id === 'rules' && 'Peraturan server yang wajib dipatuhi'}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function WikiPage() {
  const [activeSection, setActiveSection] = useState('welcome');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const currentContent = activeSection === 'welcome' ? null : wikiContent[activeSection];
  const currentSectionInfo = wikiSections.find(s => s.id === activeSection);

  // Filter sections based on search
  const filteredSections = wikiSections.filter(section =>
    section.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-16">
        <div className="flex min-h-[calc(100vh-4rem)]">
          {/* Sidebar */}
          <aside className={cn(
            'fixed lg:sticky top-16 left-0 z-40 h-[calc(100vh-4rem)] w-72 border-r border-border bg-card/95 backdrop-blur-sm transform transition-transform duration-300 lg:translate-x-0',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          )}>
            <div className="flex flex-col h-full">
              {/* Wiki Header */}
              <div className="p-6 border-b border-border">
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src={siteConfig.logo}
                    alt="Logo"
                    className="w-10 h-10 object-contain"
                  />
                  <div>
                    <h2 className="font-display font-bold text-primary text-lg">WIKIPEDIA</h2>
                    <p className="text-xs text-muted-foreground">Hytale Indonesia</p>
                  </div>
                </div>

                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Cari..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-background"
                  />
                </div>
              </div>

              {/* Navigation */}
              <ScrollArea className="flex-1 p-4">
                <nav className="space-y-1">
                  {filteredSections.map((section) => {
                    const Icon = section.icon;
                    return (
                      <button
                        key={section.id}
                        onClick={() => {
                          setActiveSection(section.id);
                          setSidebarOpen(false);
                        }}
                        className={cn(
                          'w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all group',
                          activeSection === section.id
                            ? 'bg-primary/10 text-primary'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        )}
                      >
                        <Icon className={cn(
                          'h-5 w-5 transition-colors',
                          activeSection === section.id ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'
                        )} />
                        <span className="flex-1 text-left">{section.label}</span>
                        <ChevronRight className={cn(
                          'h-4 w-4 transition-transform',
                          activeSection === section.id ? 'rotate-90 text-primary' : 'text-muted-foreground'
                        )} />
                      </button>
                    );
                  })}
                </nav>
              </ScrollArea>

              {/* Footer */}
              <div className="p-4 border-t border-border">
                <p className="text-xs text-muted-foreground text-center">
                  Powered by Hytale Indonesia
                </p>
              </div>
            </div>
          </aside>

          {/* Mobile Overlay */}
          <AnimatePresence>
            {sidebarOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-background/80 backdrop-blur-sm z-30 lg:hidden"
                onClick={() => setSidebarOpen(false)}
              />
            )}
          </AnimatePresence>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Mobile Header */}
            <div className="lg:hidden sticky top-16 z-20 bg-background border-b border-border p-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="flex items-center gap-3 text-foreground"
              >
                <Menu className="h-5 w-5" />
                <span className="font-medium">{currentSectionInfo?.label || 'Wiki'}</span>
              </button>
            </div>

            {/* Content Area */}
            <div className="max-w-4xl mx-auto p-6 md:p-10">
              <motion.article
                key={activeSection}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                {activeSection === 'welcome' ? (
                  <WelcomeSection />
                ) : (
                  <WikiPageTemplate 
                    section={activeSection}
                    content={currentContent}
                  />
                )}
              </motion.article>

              {/* Navigation at bottom */}
              {activeSection !== 'welcome' && (
                <div className="mt-12 pt-8 border-t border-border">
                  <div className="flex items-center justify-between">
                    <Button
                      variant="outline"
                      onClick={() => setActiveSection('welcome')}
                      className="flex items-center gap-2"
                    >
                      <Home className="h-4 w-4" />
                      Kembali ke Beranda
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
